/**
 * Helpers for resolving a {@link BuildingGraphFaceSideHandle} into the canonical
 * direction within the wall plane, and for retrieving the boundary polyline of any
 * handle variant.
 *
 * Resolution is fully deterministic: every handle traces back to a leaf
 * {@link BuildingGraphFaceNodeSideHandle} or {@link BuildingGraphWallPlaneSideHandle}
 * whose `faceDirection` was authored at the filter level. Compound handles
 * (`FACE_EDGE_SIDE`, `TOPOLOGY_FACE_SIDE`) preserve that direction unchanged.
 */
import type {
  BuildingGraph,
  BuildingGraphFaceNode,
  BuildingGraphFaceOutwardSideDirection,
  BuildingGraphPlanarTopology,
  BuildingGraphPlane,
} from "building-types/building/buildingGraphTypes";
import type {
  BuildingGraphFaceSide,
  BuildingGraphFaceSideDirection,
  BuildingGraphFaceSideHandle,
  BuildingGraphFaceSideLeafHandle,
} from "building-types/buildingGraph/buildingGraphSideHandles";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { ResolvedWallPlaneSide } from "building-types/walls2/wallPlane";
import type { DocFaceDirection } from "building-types/walls2/wallPlaneFilters";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { aabb2New } from "geom/aabb2Types";
import { polyLine2AABB2, polyLine2FromAABB2 } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { faceNodeGetBuildingGraphPlane } from "../buildingGraphFilter/entityFilterSelectors";
import { planarTopologyFaceToPolyLine2 } from "../buildingGraphFilter/topologyFaceGeometry";
import { faceNodeGetFace } from "./faceNodeParent";

/**
 * Recursively unwraps a face side handle to its leaf direction handle.
 * `WALL_PLANE_SIDE` and `FACE_NODE_SIDE` are returned as-is; `FACE_EDGE_SIDE` and
 * `TOPOLOGY_FACE_SIDE` traverse their `faceSide` field.
 */
export function faceSideHandleLeaf(handle: BuildingGraphFaceSideHandle): BuildingGraphFaceSideLeafHandle {
  switch (handle.type) {
    case "WALL_PLANE_SIDE":
    case "FACE_NODE_SIDE":
      return handle;
    case "FACE_EDGE_SIDE":
      return handle.faceSide;
    case "TOPOLOGY_FACE_SIDE":
      return faceSideHandleLeaf(handle.faceSide);
    default:
      return assertUnreachable(handle);
  }
}

/**
 * Resolves a face side handle into its canonical direction in the wall plane the
 * boundary belongs to.
 *
 * - `WALL_PLANE_SIDE`: the canonical direction equals the leaf's `faceDirection`
 *   (the wall plane IS the canonical plane).
 * - `FACE_NODE_SIDE`: the canonical direction equals the face node's
 *   `outwardSideDirection` if `faceDirection === "POSITIVE_NORMAL"` and inverted
 *   otherwise.
 *
 * `POSITIVE_NORMAL` -> RIGHT, `NEGATIVE_NORMAL` -> LEFT in the wall plane descriptor.
 */
export function resolveBuildingGraphFaceSide(
  handle: BuildingGraphFaceSideHandle,
  building: BuildingGraphFilterBuilding,
): BuildingGraphFaceSide | undefined {
  const leaf = faceSideHandleLeaf(handle);
  const resolvedCanonicalDirectionInPlane = resolveLeafCanonicalDirection(leaf, building);
  if (!resolvedCanonicalDirectionInPlane) {
    return undefined;
  }
  return {
    type: "FACE_SIDE",
    faceSideHandle: handle,
    resolvedCanonicalDirectionInPlane,
  };
}

export function canonicalDirectionToResolvedSide(
  direction: BuildingGraphFaceSideDirection | undefined,
): ResolvedWallPlaneSide | undefined {
  if (direction === undefined) {
    return undefined;
  }
  return direction === "POSITIVE_NORMAL" ? "RIGHT" : "LEFT";
}

function resolveLeafCanonicalDirection(
  leaf: BuildingGraphFaceSideLeafHandle,
  building: BuildingGraphFilterBuilding,
): BuildingGraphFaceSideDirection | undefined {
  if (leaf.type === "WALL_PLANE_SIDE") {
    return leaf.faceDirection;
  }
  const faceNode = building.buildingGraph.faceNodes[leaf.faceSource.faceNodeId];
  if (!faceNode) {
    return undefined;
  }
  const outwardCanonical = faceNodeOutwardDocFaceDirection(faceNode);
  if (leaf.faceDirection === "POSITIVE_NORMAL") {
    return outwardCanonical;
  }
  return invertCanonicalDirection(outwardCanonical);
}

/** DocFaceDirection of the side pointing away from the building volume on a face node. */
export function faceNodeOutwardDocFaceDirection(faceNode: BuildingGraphFaceNode): DocFaceDirection {
  return faceNode.outwardSideDirection === "WALL_PLANE_POSITIVE_NORMAL" ? "POSITIVE_NORMAL" : "NEGATIVE_NORMAL";
}

export function docFaceDirectionToOutwardSideDirection(
  direction: DocFaceDirection,
): BuildingGraphFaceOutwardSideDirection {
  return direction === "POSITIVE_NORMAL" ? "WALL_PLANE_POSITIVE_NORMAL" : "WALL_PLANE_NEGATIVE_NORMAL";
}

/** Outward DocFaceDirection for face nodes on the given wall plane. */
export function wallPlaneOutwardDocFaceDirection(
  buildingGraph: BuildingGraph,
  planeHash: string,
  faceNodeIds: readonly string[],
): DocFaceDirection | undefined {
  for (const faceNodeId of faceNodeIds) {
    const faceNode = buildingGraph.faceNodes[faceNodeId];
    if (!faceNode) {
      continue;
    }
    const plane = faceNodeGetBuildingGraphPlane(faceNode, buildingGraph);
    if (plane && plane.planeHash !== planeHash) {
      continue;
    }
    return faceNodeOutwardDocFaceDirection(faceNode);
  }
  return undefined;
}

/** Outward DocFaceDirection for a planar topology region's face nodes. */
export function planarTopologyOutwardDocFaceDirection(
  buildingGraph: BuildingGraph,
  topology: BuildingGraphPlanarTopology,
): DocFaceDirection | undefined {
  for (const faceNodeId of topology.faceNodeIds) {
    const faceNode = buildingGraph.faceNodes[faceNodeId];
    if (faceNode) {
      return faceNodeOutwardDocFaceDirection(faceNode);
    }
  }
  return undefined;
}

export function invertCanonicalDirection(direction: BuildingGraphFaceSideDirection): BuildingGraphFaceSideDirection {
  return direction === "POSITIVE_NORMAL" ? "NEGATIVE_NORMAL" : "POSITIVE_NORMAL";
}

/**
 * Returns the wall plane(s) a handle's boundary belongs to. Most handles resolve to
 * exactly one plane; a `FACE_EDGE_SIDE` may straddle two distinct planes when the
 * face edge spans both.
 */
export function faceSideHandleGetPlaneHashes(
  handle: BuildingGraphFaceSideHandle,
  building: BuildingGraphFilterBuilding,
): readonly string[] {
  switch (handle.type) {
    case "WALL_PLANE_SIDE":
      return [handle.faceSource.planeHash];
    case "FACE_NODE_SIDE": {
      const faceNode = building.buildingGraph.faceNodes[handle.faceSource.faceNodeId];
      if (!faceNode) {
        return [];
      }
      const plane = faceNodeGetBuildingGraphPlane(faceNode, building.buildingGraph);
      return plane ? [plane.planeHash] : [];
    }
    case "TOPOLOGY_FACE_SIDE": {
      const topology = building.buildingGraph.faceTopologies[handle.faceSource.topologyId];
      return topology ? [topology.planeHash] : [];
    }
    case "FACE_EDGE_SIDE": {
      const faceEdge = building.buildingGraph.faceEdges[handle.faceSource.faceEdgeIndex];
      if (!faceEdge) {
        return [];
      }
      const planes = new Set<string>();
      for (const faceNodeId of [faceEdge.faceANodeId, faceEdge.faceBNodeId]) {
        const faceNode = building.buildingGraph.faceNodes[faceNodeId];
        if (!faceNode) {
          continue;
        }
        const plane = faceNodeGetBuildingGraphPlane(faceNode, building.buildingGraph);
        if (plane) {
          planes.add(plane.planeHash);
        }
      }
      return [...planes];
    }
    default:
      return assertUnreachable(handle);
  }
}

export interface FaceSideHandleBoundary {
  readonly kind: "FACE_POLYLINE" | "PLANAR_DOMAIN";
  readonly polyline: PolyLine2;
  readonly planeHash: string;
}

/**
 * Returns the boundary polyline for the handle, expressed in the wall plane the
 * boundary belongs to. For `WALL_PLANE_SIDE`, the boundary is the AABB of the wall
 * plane's outline. For face/topology handles, it's the face polyline.
 *
 * The returned polyline is in plane-local UV coordinates of the resolved plane.
 * Returns one entry per resolved plane (most handles return one; `FACE_EDGE_SIDE`
 * may return up to two).
 */
export function faceSideHandleBoundary(
  handle: BuildingGraphFaceSideHandle,
  building: BuildingGraphFilterBuilding,
): readonly FaceSideHandleBoundary[] {
  switch (handle.type) {
    case "WALL_PLANE_SIDE": {
      const plane = building.buildingGraph.buildingPlanes[handle.faceSource.planeHash];
      if (!plane) {
        return [];
      }
      return [planarDomainBoundaryForPlane(plane)];
    }
    case "FACE_NODE_SIDE": {
      const faceNode = building.buildingGraph.faceNodes[handle.faceSource.faceNodeId];
      if (!faceNode) {
        return [];
      }
      const plane = faceNodeGetBuildingGraphPlane(faceNode, building.buildingGraph);
      if (!plane) {
        return [];
      }
      const face = faceNodeGetFace(faceNode, building.buildingGraph);
      if (!face || face.loops.length === 0) {
        return [];
      }
      // The face's outer loop is already in the face's local UV system. Callers
      // that need to project into the wall plane do so via transformFaceLoopBasis.
      // We return the face plane's hash so downstream can keep the existing
      // basis transform in WallPlaneDescriptorBuilder.
      return [
        {
          kind: "FACE_POLYLINE",
          polyline: face.loops[0],
          planeHash: plane.planeHash,
        },
      ];
    }
    case "FACE_EDGE_SIDE": {
      const faceEdge = building.buildingGraph.faceEdges[handle.faceSource.faceEdgeIndex];
      const face = faceEdge?.face;
      if (!face || face.loops.length === 0) {
        return [];
      }
      const planes = faceSideHandleGetPlaneHashes(handle, building);
      if (planes.length === 0) {
        return [];
      }
      return planes.map((planeHash) => ({
        kind: "FACE_POLYLINE",
        polyline: face.loops[0],
        planeHash,
      }));
    }
    case "TOPOLOGY_FACE_SIDE": {
      const topology = building.buildingGraph.faceTopologies[handle.faceSource.topologyId];
      if (!topology) {
        return [];
      }
      const polyline = planarTopologyFaceToPolyLine2(topology, handle.faceSource.faceIndex, true);
      return [
        {
          kind: "FACE_POLYLINE",
          polyline,
          planeHash: topology.planeHash,
        },
      ];
    }
    default:
      return assertUnreachable(handle);
  }
}

function planarDomainBoundaryForPlane(plane: BuildingGraphPlane): FaceSideHandleBoundary {
  const aabb = polyLine2AABB2(plane.planeFace.loops[0]);
  return {
    kind: "PLANAR_DOMAIN",
    polyline: polyLine2FromAABB2(aabb2New(aabb.minX, aabb.minY, aabb.maxX, aabb.maxY)),
    planeHash: plane.planeHash,
  };
}
